import { Link as RouterLink } from 'react-router-dom';
import { Box, Text, Heading } from '@primer/react';
import { ArrowRightIcon } from '@primer/octicons-react';
import Reveal from '../components/Reveal';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import { blogPosts } from '../data/blogPosts';
import { breadcrumbSchema, SITE_URL } from '../utils/seo';

const breadcrumbs = breadcrumbSchema([
  { name: 'Home', path: '/' },
  { name: 'Insights', path: '/insights' },
]);

const sortedPosts = [...blogPosts].sort((a, b) => new Date(b.date) - new Date(a.date));

function Blog() {
  return (
    <Box sx={{ backgroundColor: 'canvas.default' }}>
      <SEO
        title="Insights | UX Design, Research & Strategy Articles"
        description="Practical UX design, research, and strategy insights for enterprise, fintech, AI, and SaaS product teams, from the team at Pegasus UX."
        keywords="ux design blog, ux audit framework, ux research articles, enterprise ux insights, ux strategy blog"
        path="/insights"
        schema={breadcrumbs}
        image={`${SITE_URL}/og/insights.jpg`}
      />

      <PageHero
        title="Insights"
        subtitle="Practical UX design, research, and strategy writing for enterprise, fintech, AI, and SaaS product teams. No fluff, just the frameworks we actually use with clients."
      />

      <Box
        sx={{
          paddingX: ['24px', '48px', '100px'],
          paddingTop: [6, 7, 8],
          paddingBottom: [7, 8, 9],
        }}
      >
        <Box sx={{ maxWidth: '1400px', margin: '0 auto' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: ['1fr', '1fr', 'repeat(2, 1fr)'],
              gap: [5, 5, 6],
            }}
          >
            {sortedPosts.map((post, index) => (
              <Reveal key={post.slug} delay={index * 0.06}>
                <Box
                  as={RouterLink}
                  to={`/insights/${post.slug}`}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 5,
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'border.default',
                    textDecoration: 'none',
                    transition: 'border-color 0.2s ease, transform 0.2s ease',
                    ':hover': { borderColor: 'fg.muted', transform: 'translateY(-2px)' },
                  }}
                >
                  <Text
                    as="p"
                    sx={{
                      margin: 0,
                      marginBottom: 2,
                      fontSize: 0,
                      fontWeight: 600,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: 'accent.fg',
                    }}
                  >
                    {post.category}
                  </Text>

                  <Heading
                    as="h2"
                    sx={{
                      margin: 0,
                      marginBottom: 3,
                      fontFamily: "'Fraunces', serif",
                      fontWeight: 400,
                      fontSize: [3, 4],
                      lineHeight: 1.3,
                      color: 'fg.default',
                    }}
                  >
                    {post.title}
                  </Heading>

                  <Text as="p" sx={{ margin: 0, marginBottom: 4, fontSize: 1, lineHeight: 1.7, color: 'fg.muted' }}>
                    {post.excerpt}
                  </Text>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 'auto' }}>
                    <Text sx={{ fontSize: 0, color: 'fg.muted' }}>{post.readTime}</Text>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 0, fontWeight: 600, color: 'fg.default' }}>
                      Read the article
                      <ArrowRightIcon size={14} />
                    </Box>
                  </Box>
                </Box>
              </Reveal>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default Blog;
